import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FaCheck } from 'react-icons/fa';
import { RootState } from '../../../store/store';
import {
  FareSummary,
  prettifyServiceDescription,
} from '@skygroup/shared/utils/fareFamilies';
import './fareUpgrade.less';

/** Services shown before "All services" — keeps every card the same height. */
const VISIBLE_SERVICES = 5;

interface Props {
  summary: FareSummary;
  /** Shown when the ticket has no name (Cockpit content without fare families). */
  fallbackTitle?: string;
  /** Grand total of this ticket, gross, already formatted for display. */
  totalLabel?: string;
  /** "+ $80" / "− $20" against the current ticket; omitted for the current one. */
  diffLabel?: string;
  isCurrent?: boolean;
  /** Label of the corner badge ("Your ticket" / "Selected"). */
  badge?: string;
  ctaLabel?: string;
  ctaDisabled?: boolean;
  onChoose?: () => void;
}

/**
 * One ticket option: name, price difference, and the services the supplier
 * lists for it — included ones with a tick, paid ones marked as such. Used by
 * the view page's "Upgrade your ticket" row and by the two-one-ways fares
 * modal, so a ticket is described the same way everywhere.
 */
const FareOptionCard = ({
  summary,
  fallbackTitle,
  totalLabel,
  diffLabel,
  isCurrent = false,
  badge,
  ctaLabel,
  ctaDisabled = false,
  onChoose,
}: Props) => {
  const { viewFlight: t } = useSelector(
    (state: RootState) => state.config.translations
  );
  const [showAll, setShowAll] = useState(false);

  const title = summary.names.length
    ? summary.names.join(' + ')
    : fallbackTitle ?? '';

  const translate = (key: string, description: string) =>
    (t as any)?.[key] ?? prettifyServiceDescription(description);

  const services = showAll
    ? summary.services
    : summary.services.slice(0, VISIBLE_SERVICES);
  const hiddenCount = summary.services.length - VISIBLE_SERVICES;

  return (
    <div
      className={`fare-option${isCurrent ? ' is-current' : ''}`}
      data-testid="fare-option"
    >
      {badge && <span className="fare-option-badge">{badge}</span>}

      <div className="fare-option-head">
        <div className="fare-option-title" title={title}>
          {title}
        </div>
        {summary.isMixed && (
          <div className="fare-option-legs">
            {summary.legs.map((leg, index) => (
              <span key={`${leg.route}-${index}`}>
                {leg.route}: {leg.names.join(' + ')}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="fare-option-price">
        {diffLabel && <span className="fare-option-diff">{diffLabel}</span>}
        {totalLabel && (
          <span className="fare-option-total">
            {totalLabel} <small>{t?.totalToBePaid}</small>
          </span>
        )}
      </div>

      {summary.services.length > 0 ? (
        <ul className="fare-option-services">
          {services.map((service) => (
            <li
              key={service.key}
              className={service.included ? 'is-included' : 'is-fee'}
            >
              <span className="fare-option-service-mark" aria-hidden="true">
                {service.included ? <FaCheck /> : '$'}
              </span>
              <span className="fare-option-service-text">
                {/* supplier text is often English inside an RTL page: isolate
                    it so "1 cabin bag up to 8kg" keeps its number in front */}
                <bdi>{translate(service.key, service.description)}</bdi>
                {!service.included && t?.serviceForFee ? (
                  <em> · {t.serviceForFee}</em>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="fare-option-no-services">{t?.servicesAtBooking}</p>
      )}

      {hiddenCount > 0 && (
        <button
          type="button"
          className="fare-option-more"
          onClick={() => setShowAll((value) => !value)}
          aria-expanded={showAll}
        >
          {showAll ? t?.fewerServices : `${t?.allServices} (+${hiddenCount})`}
        </button>
      )}

      {onChoose && (
        <button
          type="button"
          className="fare-option-cta"
          onClick={onChoose}
          disabled={ctaDisabled}
        >
          {ctaLabel}
        </button>
      )}
    </div>
  );
};

export default FareOptionCard;
